import { createClient } from '@supabase/supabase-js';
import { BoardData, Card, Label, Column } from '../types/board';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Validate Supabase credentials
const isSupabaseConfigured =
  supabaseUrl &&
  supabaseAnonKey &&
  supabaseUrl.includes('supabase.co') &&
  supabaseUrl !== 'https://placeholder.supabase.co';

// Create Supabase client (will throw errors if not configured, which is handled in components)
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key'
);

export { isSupabaseConfigured };

// Database types
interface DbCard {
  id: string;
  title: string;
  local_title: string | null;
  local_plan: string | null;
  description: string | null;
  column_id: string;
  owner: string | null;
  due_date: string | null;
  wordpress_post_id: number | null;
  created_at: string;
  updated_at: string;
}

interface DbLabel {
  id: string;
  name: string;
  color: string;
  created_at: string;
}

interface DbColumn {
  id: string;
  title: string;
  position: number;
  created_at: string;
}

interface DbCardLabel {
  card_id: string;
  label_id: string;
}

// Fetch all board data
export async function fetchBoardData(): Promise<BoardData> {
  if (!isSupabaseConfigured) {
    throw new Error(
      'Supabase is not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env.local file. See SUPABASE_SCHEMA.md for setup instructions.'
    );
  }

  try {
    // Fetch all data in parallel
    const [cardsResult, labelsResult, columnsResult, cardLabelsResult] = await Promise.all([
      supabase.from('cards').select('*').order('created_at'),
      supabase.from('labels').select('*').order('created_at'),
      supabase.from('columns').select('*').order('position'),
      supabase.from('card_labels').select('*'),
    ]);

    if (cardsResult.error) throw cardsResult.error;
    if (labelsResult.error) throw labelsResult.error;
    if (columnsResult.error) throw columnsResult.error;
    if (cardLabelsResult.error) throw cardLabelsResult.error;

    const dbCards = cardsResult.data as DbCard[];
    const dbLabels = labelsResult.data as DbLabel[];
    const dbColumns = columnsResult.data as DbColumn[];
    const dbCardLabels = cardLabelsResult.data as DbCardLabel[];

    // Build labelIds map for each card
    const cardLabelMap: { [cardId: string]: string[] } = {};
    dbCardLabels.forEach((cl) => {
      if (!cardLabelMap[cl.card_id]) {
        cardLabelMap[cl.card_id] = [];
      }
      cardLabelMap[cl.card_id].push(cl.label_id);
    });

    // Transform to app format
    const cards: { [key: string]: Card } = {};
    const cardsByColumn: { [columnId: string]: string[] } = {};

    dbCards.forEach((dbCard) => {
      cards[dbCard.id] = {
        id: dbCard.id,
        title: dbCard.title,
        localTitle: dbCard.local_title || undefined,
        localPlan: dbCard.local_plan || undefined,
        description: dbCard.description || undefined,
        labelIds: cardLabelMap[dbCard.id] || [],
        owner: dbCard.owner || undefined,
        dueDate: dbCard.due_date || undefined,
        wordpressPostId: dbCard.wordpress_post_id || undefined,
      };

      // Group cards by column
      if (!cardsByColumn[dbCard.column_id]) {
        cardsByColumn[dbCard.column_id] = [];
      }
      cardsByColumn[dbCard.column_id].push(dbCard.id);
    });

    const labels: { [key: string]: Label } = {};
    dbLabels.forEach((dbLabel) => {
      labels[dbLabel.id] = {
        id: dbLabel.id,
        name: dbLabel.name,
        color: dbLabel.color,
      };
    });

    const columns: { [key: string]: Column } = {};
    const columnOrder: string[] = [];
    dbColumns.forEach((dbColumn) => {
      columns[dbColumn.id] = {
        id: dbColumn.id,
        title: dbColumn.title,
        cardIds: cardsByColumn[dbColumn.id] || [],
      };
      columnOrder.push(dbColumn.id);
    });

    return {
      cards,
      labels,
      columns,
      columnOrder,
    };
  } catch (error) {
    console.error('Error fetching board data:', error);
    throw error;
  }
}

// Create a new card
export async function createCard(card: Omit<Card, 'id'>, columnId: string): Promise<Card> {
  const { labelIds, ...cardData } = card;

  const { data, error } = await supabase
    .from('cards')
    .insert({
      title: cardData.title,
      local_title: cardData.localTitle || null,
      description: cardData.description || null,
      column_id: columnId,
      owner: cardData.owner || null,
      due_date: cardData.dueDate || null,
      wordpress_post_id: cardData.wordpressPostId || null,
    })
    .select()
    .single();

  if (error) throw error;

  const dbCard = data as DbCard;

  // Insert card-label relationships
  if (labelIds && labelIds.length > 0) {
    const cardLabelInserts = labelIds.map((labelId) => ({
      card_id: dbCard.id,
      label_id: labelId,
    }));
    const { error: labelError } = await supabase.from('card_labels').insert(cardLabelInserts);
    if (labelError) throw labelError;
  }

  return {
    id: dbCard.id,
    title: dbCard.title,
    description: dbCard.description || undefined,
    labelIds: labelIds || [],
    owner: dbCard.owner || undefined,
    dueDate: dbCard.due_date || undefined,
  };
}

// Update a card
export async function updateCard(cardId: string, updates: Partial<Card>, columnId?: string): Promise<void> {
  const { labelIds, ...cardData } = updates;

  const updateData: any = {};
  if (cardData.title !== undefined) updateData.title = cardData.title;
  if (cardData.localTitle !== undefined) updateData.local_title = cardData.localTitle || null;
  if (cardData.localPlan !== undefined) updateData.local_plan = cardData.localPlan || null;
  if (cardData.description !== undefined) updateData.description = cardData.description || null;
  if (cardData.owner !== undefined) updateData.owner = cardData.owner || null;
  if (cardData.dueDate !== undefined) updateData.due_date = cardData.dueDate || null;
  if (cardData.wordpressPostId !== undefined) updateData.wordpress_post_id = cardData.wordpressPostId || null;
  if (columnId !== undefined) updateData.column_id = columnId;

  if (Object.keys(updateData).length > 0) {
    const { error } = await supabase.from('cards').update(updateData).eq('id', cardId);
    if (error) throw error;
  }

  // Update labels if provided
  if (labelIds !== undefined) {
    // Delete existing card-label relationships
    await supabase.from('card_labels').delete().eq('card_id', cardId);

    // Insert new relationships
    if (labelIds.length > 0) {
      const cardLabelInserts = labelIds.map((labelId) => ({
        card_id: cardId,
        label_id: labelId,
      }));
      const { error: labelError } = await supabase.from('card_labels').insert(cardLabelInserts);
      if (labelError) throw labelError;
    }
  }
}

// Delete a card
export async function deleteCard(cardId: string): Promise<void> {
  const { error } = await supabase.from('cards').delete().eq('id', cardId);
  if (error) throw error;
}

// Move a card to a different column
export async function moveCard(cardId: string, newColumnId: string): Promise<void> {
  const { error } = await supabase.from('cards').update({ column_id: newColumnId }).eq('id', cardId);
  if (error) throw error;
}

// Update card labels (for drag between swimlanes)
export async function updateCardLabels(cardId: string, labelIds: string[]): Promise<void> {
  // Delete existing relationships
  await supabase.from('card_labels').delete().eq('card_id', cardId);

  // Insert new relationships
  if (labelIds.length > 0) {
    const cardLabelInserts = labelIds.map((labelId) => ({
      card_id: cardId,
      label_id: labelId,
    }));
    const { error } = await supabase.from('card_labels').insert(cardLabelInserts);
    if (error) throw error;
  }
}

// Fetch all owners
export async function fetchOwners(): Promise<string[]> {
  const { data, error } = await supabase
    .from('owners')
    .select('name')
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching owners:', error);
    return [];
  }

  return data.map((owner) => owner.name);
}
